import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { CH_STATUS_AR, CH_STATUS_TONE } from "@/v2/lib/rbac";
import { Chip, Skeleton, Modal, Confirm, Table, PageHead, UploadBtn } from "@/v2/admin/ui";
import { Swords, Plus, Pencil, Trash2, Copy, ArrowUp, ArrowDown, ChevronDown } from "lucide-react";

function ChallengeForm({ ch, onClose }) {
  const qc = useQueryClient();
  const isNew = !ch?.id;
  const [f, setF] = useState({
    day: ch?.day || "", title: ch?.title || "", description: ch?.description || "",
    content: ch?.content || "", points: ch?.points || 50, pass_score: ch?.pass_score || 70,
    video_duration: ch?.video_duration || "٥ دقائق", thumbnail: ch?.thumbnail || "",
    skills: (ch?.skills || []).join("، "), objectives: (ch?.objectives || []).join("\n"),
    status: ch?.status || "draft", scheduled_at: ch?.scheduled_at || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title.trim()) return toast.error("العنوان مطلوب");
    setSaving(true);
    try {
      const body = {
        ...f, day: Number(f.day) || undefined, points: Number(f.points), pass_score: Number(f.pass_score),
        skills: f.skills.split(/[،,]/).map((s) => s.trim()).filter(Boolean),
        objectives: f.objectives.split("\n").map((s) => s.trim()).filter(Boolean),
        scheduled_at: f.scheduled_at || undefined,
      };
      if (isNew) await api.post("/admin/challenges", body);
      else await api.put(`/admin/challenges/${ch.id}`, body);
      toast.success(isNew ? "تم إنشاء التحدي" : "تم حفظ التعديلات");
      qc.invalidateQueries({ queryKey: ["adm-challenges"] });
      qc.invalidateQueries({ queryKey: ["challenges"] });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحفظ"); } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? "إنشاء تحدٍ جديد" : `تعديل: ${ch.title}`} onClose={onClose} wide testId="ch-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="v2-label">العنوان</label><input value={f.title} onChange={set("title")} data-testid="ch-title" className="v2-input" /></div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="v2-label">اليوم</label><input type="number" value={f.day} onChange={set("day")} data-testid="ch-day" className="v2-input" /></div>
          <div><label className="v2-label">النقاط</label><input type="number" value={f.points} onChange={set("points")} data-testid="ch-points" className="v2-input" /></div>
          <div><label className="v2-label">النجاح %</label><input type="number" value={f.pass_score} onChange={set("pass_score")} data-testid="ch-pass" className="v2-input" /></div>
        </div>
        <div className="sm:col-span-2"><label className="v2-label">الوصف المختصر</label><textarea rows={2} value={f.description} onChange={set("description")} data-testid="ch-desc" className="v2-input resize-none" /></div>
        <div className="sm:col-span-2"><label className="v2-label">المحتوى التعليمي / الملخص</label><textarea rows={3} value={f.content} onChange={set("content")} className="v2-input resize-none" /></div>
        <div><label className="v2-label">الوقت المتوقع</label><input value={f.video_duration} onChange={set("video_duration")} className="v2-input" /></div>
        <div>
          <label className="v2-label">صورة التحدي (Thumbnail)</label>
          <div className="flex gap-2">
            <input value={f.thumbnail} onChange={set("thumbnail")} dir="ltr" className="v2-input" placeholder="https://..." />
            <UploadBtn accept="image/*" label="رفع" testId="ch-thumb-upload" onDone={(r) => setF({ ...f, thumbnail: r.url })} />
          </div>
        </div>
        <div><label className="v2-label">المهارات المستهدفة (افصل بفاصلة)</label><input value={f.skills} onChange={set("skills")} className="v2-input" /></div>
        <div><label className="v2-label">الحالة</label>
          <select value={f.status} onChange={set("status")} data-testid="ch-status-select" className="v2-input">
            {Object.entries(CH_STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {f.status === "scheduled" && (
          <div><label className="v2-label">تاريخ فتح التحدي</label><input type="datetime-local" dir="ltr" value={f.scheduled_at?.slice(0, 16) || ""} onChange={(e) => setF({ ...f, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="ch-scheduled" className="v2-input" /></div>
        )}
        <div className="sm:col-span-2"><label className="v2-label">الأهداف التعليمية (سطر لكل هدف)</label><textarea rows={3} value={f.objectives} onChange={set("objectives")} className="v2-input resize-none" /></div>
      </div>
      <button onClick={save} disabled={saving} data-testid="ch-save" className="v2-btn w-full mt-5">{saving ? "جارٍ الحفظ..." : "حفظ التحدي"}</button>
    </Modal>
  );
}

export default function Challenges() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [delId, setDelId] = useState(null);
  const [statusMenu, setStatusMenu] = useState(null);
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["adm-challenges"], queryFn: async () => (await api.get("/admin/challenges")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["adm-challenges"] });
    qc.invalidateQueries({ queryKey: ["challenges"] });
  };

  const setStatus = async (cid, status) => {
    try { await api.patch(`/admin/challenges/${cid}/status`, { status }); toast.success(`تم تغيير الحالة إلى «${CH_STATUS_AR[status]}»`); invalidate(); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر التغيير"); }
    setStatusMenu(null);
  };
  const duplicate = async (cid) => {
    try { await api.post(`/admin/challenges/${cid}/duplicate`); toast.success("تم إنشاء نسخة (مسودة)"); invalidate(); }
    catch { toast.error("تعذّر النسخ"); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/challenges/${delId}`); toast.success("تم حذف التحدي وتسجيل العملية"); invalidate(); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحذف"); }
    setDelId(null);
  };
  const move = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= rows.length) return;
    const ids = rows.map((r) => r.id);
    [ids[idx], ids[t]] = [ids[t], ids[idx]];
    try { await api.post("/admin/challenges/reorder", { ids }); invalidate(); }
    catch { toast.error("تعذّر إعادة الترتيب"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-challenges">
      <PageHead icon={Swords} title="إدارة التحديات" sub="إنشاء وتعديل ونسخ وإعادة ترتيب وحذف مع حالات كاملة.">
        <button onClick={() => setForm({})} data-testid="new-challenge" className="v2-btn v2-btn-sm"><Plus size={16} /> إنشاء تحدٍ</button>
      </PageHead>

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <Table head={["الترتيب", "اليوم", "العنوان", "المسار", "النقاط", "النجاح", "الأسئلة", "الملفات", "الحالة", "إجراءات"]} min={980} testId="ch-table">
          {rows.map((c, i) => (
            <tr key={c.id} data-testid={`ch-row-${c.day}`}>
              <td>
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`ch-up-${c.day}`} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowUp size={13} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowDown size={13} /></button>
                </div>
              </td>
              <td className="font-bold v2-gold-text">{c.day}</td>
              <td className="font-semibold max-w-[240px] truncate">{c.title}</td>
              <td>{c.path_name ? <Chip tone="muted">{c.path_name}</Chip> : <span className="text-[#bcc8cf]">—</span>}</td>
              <td>{c.points}</td>
              <td>{c.pass_score}%</td>
              <td>{c.questions?.length || 0}</td>
              <td>{(c.files || []).length}</td>
              <td className="relative">
                <button onClick={() => setStatusMenu(statusMenu === c.id ? null : c.id)} data-testid={`ch-status-${c.day}`} className="inline-flex">
                  <Chip tone={CH_STATUS_TONE[c.status] || "muted"}>{CH_STATUS_AR[c.status] || c.status} <ChevronDown size={11} /></Chip>
                </button>
                {statusMenu === c.id && (
                  <div className="absolute z-30 top-full start-0 mt-1 v2-glass p-2 w-36 space-y-1" style={{ boxShadow: "0 20px 50px -20px rgba(0,0,0,.7)" }}>
                    {Object.entries(CH_STATUS_AR).map(([k, v]) => (
                      <button key={k} onClick={() => setStatus(c.id, k)} data-testid={`ch-setstatus-${k}`}
                        className="w-full text-start text-xs font-bold px-3 py-2 rounded-lg hover:bg-white/10"
                        style={{ background: c.status === k ? "rgba(187,150,97,0.15)" : "transparent" }}>{v}</button>
                    ))}
                  </div>
                )}
              </td>
              <td>
                <div className="flex gap-1.5">
                  <button onClick={() => setForm(c)} title="تعديل" data-testid={`ch-edit-${c.day}`} className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center hover:opacity-75"><Pencil size={14} /></button>
                  <button onClick={() => duplicate(c.id)} title="نسخ" data-testid={`ch-dup-${c.day}`} className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center hover:opacity-75"><Copy size={14} /></button>
                  <button onClick={() => setDelId(c.id)} title="حذف" data-testid={`ch-del-${c.day}`} className="w-8 h-8 rounded-lg flex items-center justify-center hover:opacity-75 bg-[#B45309]/20 text-[#F7A08A]"><Trash2 size={14} /></button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {form !== null && <ChallengeForm ch={form.id ? form : null} onClose={() => setForm(null)} />}
      {delId && (
        <Confirm title="حذف التحدي نهائيًا" danger confirmLabel="نعم، احذف"
          body="سيتم حذف التحدي وجميع أسئلته وملفاته نهائيًا. ستُسجَّل العملية في سجل الإدارة ولا يمكن التراجع عنها. هل أنت متأكد؟"
          onConfirm={remove} onClose={() => setDelId(null)} testId="ch-del-confirm" />
      )}
    </div>
  );
}
